import { Button, Card, Collapse, Divider, Drawer, Empty, Form, Grid, Input, Link, Message, Modal, Popconfirm, Popover, Select, Space, Table, Tabs, Tooltip, Typography } from "@arco-design/web-react";
import { useCallback, useEffect, useState } from "react";
import { AccountAPI, GLAPI, JournalAPI, TransactionAPI } from "@/apis/accounting";
import { InvAdjustmentOrderAPI } from "@/apis/material";
import _ from "lodash";
import { IconCheckCircle, IconMinusCircle, IconEdit, IconLock, IconPlus } from "@arco-design/web-react/icon";
import { AccountCategoryDef, AccountSource, AccountTypeDef } from "@/enums/accounting";
import { GLActionDef, GLImpactType } from "@/enums/common";
import { Status } from "@/enums/status";
import { buildEnumOptions } from "@/enums/utils";
import { Currency } from "@/components/Numeric";
import { EnumLabel, EnumSelect } from "@/components/Enum";
import { formatDate } from "@/utils";

const TabPane = Tabs.TabPane;
const Option = Select.Option;
const CollapseItem = Collapse.Item;
const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;

const AccountForm = (props) => {
    const [form] = Form.useForm();
    const [defaultValue] = useState(props.data);

    const onSubmit = async () => {
        let formData = await form.validate();
        if (!formData.id) {
            await AccountAPI.add(formData);
            Message.success("Account created successfully");
        } else {
            await AccountAPI.update(formData.id, formData);
            Message.success("Account updated successfully");
        }

        props.onSubmit && props.onSubmit();
    }

    return (
        <Modal title={props.data.id ? "Account" : "Create New Account"} visible
            onOk={onSubmit}
            onCancel={props.onCancel}>
            <Form form={form}
                initialValues={defaultValue}
                labelCol={{ span: 7 }} wrapperCol={{ span: 16 }}>
                <FormItem label="ID" field="id" hidden>
                    <Input />
                </FormItem>
                <FormItem label="Company ID" field="companyId" hidden>
                    <Input />
                </FormItem>
                <FormItem label="Account Type" field="accountType">
                    <EnumSelect enumObj={AccountTypeDef} />
                </FormItem>
                <FormItem label="Account ID" field="accountNo">
                    <Input />
                </FormItem>
                <FormItem label="Account Name" field="accountName" rules={[{ required: true, message: "Account name is required" }]}>
                    <Input readOnly={props.data && props.data.source !== AccountSource.Manual} />
                </FormItem>
                <FormItem label="Description" field="description">
                    <Input.TextArea />
                </FormItem>
            </Form>
        </Modal >
    );
}

export const ChartOfAccounts = () => {
    const [categories, setCategories] = useState([]);
    const [activeTypes, setActiveTypes] = useState([]);
    const [accountFormVisible, setAccountFormVisible] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState({});
    const [status, setStatus] = useState(Status.Active);

    const getAccounts = useCallback(async () => {
        let categoryItems = buildEnumOptions(AccountCategoryDef);
        
        // Build types from local enum (category is already included in AccountTypeDef)
        let types = buildEnumOptions(AccountTypeDef);
        
        let typesByCategory = _.groupBy(types, type => type.category);
        _.forEach(categoryItems, c => {
            c.types = typesByCategory[c.value] || [];
        });

        let accounts = await AccountAPI.list();
        if (status !== 'All') {
            accounts = _.filter(accounts, account => account.status === status);
        }

        let accountsByType = _.groupBy(accounts, account => account.accountType);
        _.forEach(types, type => {
            type.accounts = accountsByType[type.value] || [];
            type.count = type.accounts.length;
        });
        let actives = _.flatMap(types.filter(type => type.count > 0), type => type.value);
        setActiveTypes(actives);

        setCategories(categoryItems);
    }, [status]);

    useEffect(() => {
        getAccounts();
    }, [getAccounts]);

    const handleEnable = async (id) => {
        try {
            await AccountAPI.open(id);
            Message.success("Account enabled successfully");
            getAccounts();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const handleDisable = async (id) => {
        try {
            await AccountAPI.close(id);
            Message.success("Account disabled successfully");
            getAccounts();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const NewAccountButton = (props) => {
        return (
            <Button type="secondary" shape="circle" icon={<IconPlus />} size="mini"
                onClick={() => {
                    setSelectedAccount({
                        accountType: props.typeId,
                        source: AccountSource.Manual
                    });
                    setAccountFormVisible(true);
                }}
            ></Button>
        );
    };

    const AccountTransactionButton = ({ account }) => {
        const [visible, setVisible] = useState(false);
        const [glData, setGlData] = useState([]);
        const [glPagination, setGlPagination] = useState({ current: 1, total: 0 });
        const [transData, setTransData] = useState([]);
        const [transPagination, setTransPagination] = useState({ current: 1, total: 0 });
        const [journalData, setJournalData] = useState([]);
        const [journalPagination, setJournalPagination] = useState({ current: 1, total: 0 });
        const [adjustmentData, setAdjustmentData] = useState([]);
        const [adjustmentPagination, setAdjustmentPagination] = useState({ current: 1, total: 0 });

        const glOrderUrl = (gl) => {
            switch (gl.impactType) {
                case GLImpactType.Receipt:
                    return `/workspace/purchasing/receipt/${gl.impactFrom}`;
                case GLImpactType.Bill:
                    return `/workspace/purchasing/bills/${gl.impactFrom}`;
                case GLImpactType.SOFulfillment:
                    return `/workspace/sales/so-fulfillment/${gl.impactFrom}`;
                case GLImpactType.Invoice:
                    return `/workspace/sales/invoice/${gl.impactFrom}`;
                case GLImpactType.WoFulfillment:
                    return `/workspace/manufacturing/wo-fulfillment/${gl.impactFrom}`;
                case GLImpactType.InventoryAdjustment:
                    return `/workspace/material/inventory_adjustment/${gl.impactFrom}`;
                default:
                    return null;
            }
        }

        const openGLOrder = (gl) => {
            const w = window.open('_blank');
            w.location.href = glOrderUrl(gl);
        }

        const getGLImpacts = async (page = 1) => {
            const values = await GLAPI.query(page, null, null, { accountId: account.id });
            setGlData(values.records);
            setGlPagination({ ...glPagination, current: page, total: values.total });
        }

        const getTransactions = async (page = 1) => {
            const values = await TransactionAPI.query(page, { accountIds: [account.id] });
            setTransData(values.records);
            setTransPagination({ ...transPagination, current: page, total: values.total });
        }

        const getJournals = async (page = 1) => {
            const values = await JournalAPI.query(page, { accountId: account.id });
            setJournalData(values.records);
            setJournalPagination({ ...journalPagination, current: page, total: values.total });
        }

        const getAdjustments = async (page = 1) => {
            const values = await InvAdjustmentOrderAPI.query({ accountId: account.id, page });
            setAdjustmentData(values.records);
            setAdjustmentPagination({ ...adjustmentPagination, current: page, total: values.total });
        }

        const loadData = () => {
            getGLImpacts();
            getTransactions();
            getJournals();
            getAdjustments();
        }

        return (
            <>
                <Button type="text" size="small" style={{ padding: 0 }} onClick={() => {
                    loadData();
                    setVisible(true);
                }}>
                    {account.accountName}
                </Button>
                <Drawer title={`Account Log: ${account.accountName}`}
                    visible={visible}
                    width={800}
                    footer={
                        <Button type="primary" onClick={() => setVisible(false)}>Close</Button>
                    }
                    onCancel={() => setVisible(false)}>
                    <Typography.Title heading={6}>GL Impact</Typography.Title>
                    <Table
                        rowKey="id"
                        columns={[
                            { title: "Date", dataIndex: "impactDate", width: 120 },
                            {
                                title: "Order#", render: (col, record) => {
                                    const url = glOrderUrl(record);
                                    return url
                                        ? <Link onClick={() => openGLOrder(record)}>{record.orderNo}</Link>
                                        : record.orderNo;
                                }
                            },
                            { title: "Item", dataIndex: "itemName" },
                            { title: "Action", render: (col, record) => <EnumLabel enumObj={GLActionDef} value={record.action} /> },
                            {
                                title: "Amount", render: (col, record) => <Currency value={record.totalAmount} />
                            },
                        ]}
                        data={glData}
                        pagination={glPagination}
                        onChange={(pagination) => getGLImpacts(pagination.current)}
                    />

                    <Typography.Title heading={6} style={{ marginTop: 20 }}>Transactions</Typography.Title>
                    <Table
                        rowKey="id"
                        columns={[
                            { title: "Date", dataIndex: "date", width: 120 },
                            { title: "Account", dataIndex: "accountName" },
                            {
                                title: "Amount",
                                render: (col, record) => <Currency value={record.amount} />
                            },
                        ]}
                        data={transData}
                        pagination={transPagination}
                        onChange={(pagination) => getTransactions(pagination.current)}
                    />

                    <Typography.Title heading={6} style={{ marginTop: 20 }}>Journal Entries</Typography.Title>
                    <Table
                        rowKey="id"
                        columns={[
                            { title: "Date", dataIndex: "date", width: 120 },
                            { title: "Account", dataIndex: "accountName" },
                            {
                                title: "Amount",
                                render: (col, record) => <Currency value={record.debit || record.credit} />
                            },
                        ]}
                        data={journalData}
                        pagination={journalPagination}
                        onChange={(pagination) => getJournals(pagination.current)}
                    />

                    <Typography.Title heading={6} style={{ marginTop: 20 }}>Inventory Adjustments</Typography.Title>
                    <Table
                        rowKey="id"
                        columns={[
                            {
                                title: "Date", dataIndex: "createTime", width: 120,
                                render: (val) => val ? formatDate(val) : ""
                            },
                            { title: "ID", dataIndex: "adjustmentNo" },
                            {
                                title: "Amount", dataIndex: "adjustmentValue",
                                render: (val) => val != null ? <Currency value={val} /> : ""
                            },
                        ]}
                        data={adjustmentData}
                        pagination={adjustmentPagination}
                        onChange={(pagination) => getAdjustments(pagination.current)}
                    />
                </Drawer>
            </>
        )
    }


    return (
        <Card title="Chart of Accounts" extra={
            <Space>
                <Select value={status} style={{ width: 120 }} onChange={(val) => setStatus(val)}>
                    <Option value={Status.Active}>Active</Option>
                    <Option value={Status.Inactive}>Inactive</Option>
                    <Option value="All">All</Option>
                </Select>
            </Space>
        }>
            <Tabs defaultActiveTab="1">
                {categories.map(category =>
                    <TabPane key={category.value} title={category.label}>
                        <Collapse activeKey={activeTypes} lazyload={true}
                            onChange={(key, keys, e) => setActiveTypes(keys)}>
                            {category.types && category.types
                                .map((type) =>
                                    <CollapseItem header={type.label + " (" + type.count + ")"}
                                        name={type.value} key={type.value} extra={<NewAccountButton typeId={type.value} />}>
                                        {type.accounts.map((account, index) =>
                                            <Row key={account.id}>
                                                {index > 0 &&
                                                    <Divider style={{ marginTop: 5, marginBottom: 5 }} />
                                                }

                                                <Col span={12}>
                                                    <span className="primary-text">
                                                        {account.source === AccountSource.System &&
                                                            <Popover content="This Account is created by system."><IconLock /></Popover>
                                                        }
                                                        <AccountTransactionButton account={account} />
                                                    </span>
                                                    <Tooltip content="Edit">
                                                        <Button type="text" shape="circle" icon={<IconEdit />} onClick={() => {
                                                            setSelectedAccount(account);
                                                            setAccountFormVisible(true);
                                                        }}></Button>
                                                    </Tooltip>
                                                    {account.source !== AccountSource.System && account.status === Status.Inactive && (
                                                        <Tooltip content="Enable">
                                                            <Popconfirm title="Are you sure you want to enable this account?" onOk={() => handleEnable(account.id)}>
                                                                <Button type="text" shape="circle" icon={<IconCheckCircle />} style={{ color: 'rgb(var(--green-6))' }} />
                                                            </Popconfirm>
                                                        </Tooltip>
                                                    )}
                                                    {account.source !== AccountSource.System && account.status === Status.Active && (
                                                        <Tooltip content="Disable">
                                                            <Popconfirm title="Are you sure you want to disable this account?" onOk={() => handleDisable(account.id)}>
                                                                <Button type="text" shape="circle" icon={<IconMinusCircle />} style={{ color: 'rgb(var(--red-6))' }} />
                                                            </Popconfirm>
                                                        </Tooltip>
                                                    )}<br />
                                                    <span className="normal-text">Account ID: {account.accountNo}</span><br />
                                                </Col>
                                                <Col span={12}>
                                                    <span>{account.description}</span>
                                                </Col>
                                            </Row>
                                        )}
                                        {type.count <= 0 &&
                                            <Empty />
                                        }
                                    </CollapseItem>
                                )}
                        </Collapse>

                    </TabPane>
                )}

            </Tabs>
            {accountFormVisible &&
                <AccountForm data={selectedAccount}
                    onSubmit={() => {
                        setAccountFormVisible(false);
                        getAccounts();
                    }}
                    onCancel={() => setAccountFormVisible(false)} />
            }
        </Card>
    );
}